import { useTranslations } from '@aikoa/i18n/react'
import { Pressable, View } from 'react-native'
import Animated from 'react-native-reanimated'
import Icon from '~/components/Icon.tsx'
import { Button } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

export type SortDirection = 'asc' | 'desc'

export interface FilterOption<Tf extends string> {
  key: Tf
  label: string
}

export interface SortOption<Ts = string> {
  key: Ts
  label: string
  direction?: SortDirection
}

interface SortBarProps<Ts = string> {
  sortOptions: SortOption<Ts>[]
  currentSort: Ts
  onSortChange: (sort: Ts) => void
  sortDirection: SortDirection
  onSortDirectionChange: (direction: SortDirection) => void
  className?: string
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

export function SortBar<Ts extends string>({
  sortOptions,
  currentSort,
  onSortChange,
  sortDirection,
  onSortDirectionChange,
  className,
}: SortBarProps<Ts>) {
  const { t } = useTranslations('common')

  return (
    <View className={cn('w-full flex-row flex-wrap items-center justify-start gap-2', className)}>
      <View className="flex-1 basis-full flex-row items-center gap-3 sm:mr-auto sm:flex-auto sm:basis-auto">
        <Text className="font-light text-xs text-secondary">{t(($) => $.actions.sort)}</Text>
        <Button
          variant="outline"
          size="icon"
          className="aspect-square size-7"
          onPress={() => onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc')}>
          <Icon name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} size={12} />
        </Button>
      </View>
      <View className="flex-row items-center justify-end gap-2">
        {sortOptions.map((option) => (
          <AnimatedPressable
            key={option.key}
            hitSlop={14}
            className={cn(
              'rounded-full border border-tertiary-foreground px-4 py-1 transition-all duration-150 active:scale-95',
              currentSort === option.key && 'border-accent/50 bg-accent/10'
            )}
            onPress={() => onSortChange(option.key)}>
            <Text
              className={cn(
                'select-none font-medium text-xs text-primary/90',
                currentSort === option.key && 'text-accent'
              )}>
              {option.label}
            </Text>
          </AnimatedPressable>
        ))}
      </View>
    </View>
  )
}

function FilterChip({
  label,
  isActive,
  onPress,
}: {
  label: string
  isActive: boolean
  onPress: () => void
}) {
  return (
    <Pressable
      onPress={onPress}
      hitSlop={6}
      className={cn(
        'group/chip rounded-full transition-all duration-150 ease-out active:scale-95'
      )}>
      <Text
        className={cn(
          'underline-offset-3 select-none font-medium text-sm underline transition-colors duration-150 ease-out',
          isActive ? 'text-accent' : 'text-primary/70 group-hover/chip:text-primary'
        )}>
        {label}
      </Text>
    </Pressable>
  )
}

export function FilterBar<Tf extends string>({
  filterOptions,
  activeFilters,
  onFilterToggle,
  className,
  label,
}: {
  filterOptions: FilterOption<Tf>[]
  activeFilters: Set<Tf>
  onFilterToggle: (key: Tf) => void
  className?: string
  label?: string
}) {
  const { t } = useTranslations('common')

  return (
    <View className={cn('flex-row flex-wrap items-center justify-start gap-2', className)}>
      <View className="mr-auto flex-row items-center justify-start">
        <Text className="font-light text-xs text-secondary">
          {label ?? t(($) => $.actions.filter)}
        </Text>
      </View>
      <View className="flex-row items-center justify-start gap-4">
        {filterOptions.map((option) => (
          <FilterChip
            key={option.key}
            label={option.label}
            isActive={activeFilters.has(option.key)}
            onPress={() => onFilterToggle(option.key)}
          />
        ))}
      </View>
    </View>
  )
}
